import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["fileInput", "dropzone", "submit", "confirmWrapper"];

  constructor() {
    super(...arguments);
    this.fileArray = [];
    this.uploadedCount = 0;
  }

  // Submit stays inactive until every added file has finished uploading AND the date of test
  // analysis is filled in. The date field lives in the Test Information panel outside this
  // component, so it is found from the document (pages without one, like the component
  // preview, only gate on the uploads).
  refreshSubmitGate() {
    if (!this.hasSubmitTarget) return;

    const dateInput = document.querySelector('input[type="date"]');
    const dateComplete = dateInput ? dateInput.value !== "" : true;
    const uploadsComplete = this.uploadedCount > 0 && this.fileArray.length === 0;

    this.submitTarget.toggleAttribute("disabled", !(uploadsComplete && dateComplete));
  }

  // Back returns the page to the entry-method chooser, so everything collected so far is
  // discarded: the signed ids ride along in hidden inputs inside the progress rows, and leaving
  // them in place would attach the abandoned uploads to whatever is submitted next.
  reset() {
    this.fileArray = [];
    this.uploadedCount = 0;
    this.fileInputTarget.value = "";
    this.#clearProgressBars();

    if (this.hasConfirmWrapperTarget) {
      this.confirmWrapperTarget.classList.add("hidden");
    }

    this.refreshSubmitGate();
    this.dispatch("reset");
  }

  #clearProgressBars() {
    const list = document.getElementById("progress-bar-list");
    if (!list) return;

    Array.from(list.children)
      .filter((child) => child.tagName !== "TEMPLATE")
      .forEach((child) => child.remove());
  }

  onDragOver(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.add("dragging");
  }

  onDrop(event) {
    const files = event.dataTransfer.files;
    event.preventDefault();
    this.dropzoneTarget.classList.remove("dragging");
    this.addFiles(files);
  }

  uploadFiles(event) {
    const files = event.target.files;
    this.addFiles(files);
  }

  addFiles(files) {
    if (this.hasConfirmWrapperTarget) {
      this.confirmWrapperTarget.classList.remove("hidden");
    }
    this.dispatch("filesAdded");

    const url = this.fileInputTarget.dataset.directUploadUrl;

    Array.from(files).forEach(file => {
      this.fileArray.push(file);
      this.#addFile(file, url)
    });

    this.refreshSubmitGate();
    this.fileInputTarget.value = "";
  }

  #addFile(file, url) {
    const progressNode = this.#displayFileUpload(file);
    const delegate = this.#createUploadDelegate(progressNode);
    const upload = new DirectUpload(file, url, delegate);
    upload.create((error, blob) => this.#onFileUpload(error, progressNode, blob, file));
  }

  #displayFileUpload(file) {
    const fileName = file.name;
    const fileType = file.type.split("/").at(-1).toUpperCase();
    const template = document.getElementById("progress-bar-template");
    const progress = document.getElementById("progress-bar-list");
    const fragment = template.content.cloneNode(true);

    // Set the file name and type in the cloned fragment
    const name = fragment.querySelector(".file-name");
    name.textContent = fileName;
    const type = fragment.querySelector(".file-type");
    type.textContent = fileType;

    progress.appendChild(fragment);
    return progress.lastElementChild;
  }

  #createUploadDelegate(progressNode) {
    return {
      directUploadWillStoreFileWithXHR: (request) => {
        request.upload.addEventListener("progress", (event) => {
          this.#directUploadDidProgress(event, progressNode);
        });
      }
    };
  }

  #directUploadDidProgress(event, progressNode) {
    const percent = Math.round((event.loaded / event.total) * 100);
    const progressBar = progressNode.querySelector(".bar");
    const percentText = progressNode.querySelector(".percent");
    progressBar.style.width = `${percent}%`;
    percentText.textContent = `${percent}%`;
  }

  #onFileUpload(error, progressNode, blob, file) {
    if (error) {
      console.error(error);
    } else {
      const input = document.createElement("input");
      input.type = "hidden"
      input.name = "blood_tests_create[source_document_signed_ids][]"
      input.value = blob.signed_id
      progressNode.appendChild(input)
      const indexOfFileInArray = this.fileArray.indexOf(file);
      this.fileArray.splice(indexOfFileInArray, 1);
      this.uploadedCount += 1;

      this.refreshSubmitGate();
    }
  }
}
